import React from "react";
import InputLabel from "../input/InputLabel";
import classNames from "classnames";
import "./TextArea.scss";
import { useRequiredValidation } from "../../custom-hooks/useRequiredValidation";
import Validations from "../validations/Validations";
import { withDefaults } from "../../utils/withDefaults";
import type { Placement } from "@popperjs/core";

type TextAreaEvent = React.ChangeEvent<HTMLTextAreaElement>;

export interface TextAreaProps {
  id?: string;
  ariaLabel?: string;
  className?: string;
  name?: string;
  value?: string;
  onChange?: (event: TextAreaEvent) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Runs after onChange; AutoSizeTextArea uses it to re-measure. */
  changeFn?: () => void;
  label?: string;
  helpText?: string;
  helpTextPlacement?: Placement;
  required?: boolean;
  validations?: string[];
  placeholder?: string;
  reference?: React.Ref<HTMLTextAreaElement>;
  additionalStyles?: React.CSSProperties;
  rows?: number;
  hasBottomPadding?: boolean;
  /** Runs on events that change the value outside onChange (paste, cut, drop). */
  delayedChangeFn?: () => void;
}

export default function TextArea(incomingProps: TextAreaProps) {
  const props = withDefaults(incomingProps, textAreaDefaultProps);

  const validate = useRequiredValidation(props.required, props.value);
  const allValidations = validate.validations.concat(props.validations);

  return (
    <div
      className={classNames(
        "paragraph-field",
        props.hasBottomPadding ? "has-bottom-padding" : ""
      )}
    >
      {props.label ? (
        <InputLabel
          label={props.label}
          helpText={props.helpText}
          helpTextPlacement={props.helpTextPlacement}
          isRequired={props.required}
        />
      ) : null}
      <textarea
        ref={props.reference}
        id={props.id}
        aria-label={props.ariaLabel}
        className={classNames(
          "text-area",
          props.className,
          allValidations.length > 0 ? "has-validation-error" : null
        )}
        name={props.name}
        value={props.value}
        onChange={(event: TextAreaEvent) => {
          props.onChange(event);
          props.changeFn();
        }}
        onBlur={props.onBlur}
        onKeyDown={props.delayedChangeFn}
        onPaste={props.delayedChangeFn}
        onDrop={props.delayedChangeFn}
        onCut={props.delayedChangeFn}
        required={props.required}
        onInvalid={validate.onInvalid}
        placeholder={props.placeholder}
        style={props.additionalStyles}
        rows={props.rows}
      />
      <Validations validations={allValidations} />
    </div>
  );
}

const textAreaDefaultProps = {
  ariaLabel: "text-area",
  className: "",
  name: "",
  value: "",
  onChange: () => {},
  onBlur: () => {},
  changeFn: () => {},
  label: "",
  helpText: "",
  helpTextPlacement: "bottom" as Placement,
  required: false,
  validations: [] as string[],
  placeholder: "",
  additionalStyles: {},
  rows: 4,
  hasBottomPadding: true
};
